import _ from 'lodash';
import CacheableJson from '../CacheableJson';
import { Round } from '../../models';

const ACTUAL_ROUND_STATUSES = [
	'scheduled',
	'active'
];

const SCHEDULED_ROUND_STATUSES = [
	'scheduled'
];

const ACTIVE_ROUND_STATUSES = [
	'active'
];

const COMPLETED_ROUND_STATUSES = [
	'complete'
];

const bye_rounds_ids = JSON.parse(process.env.BYE_ROUNDS || '[]');

export class Rounds extends CacheableJson {

	get model() {
		return Round;
	}

	get is_preseason() {
		return Rounds.isScheduled(this.first().get('status'));
	}
	get is_endseason() {
		return this.last().get('status') === 'complete';
	}
	get is_last_round_started() {
		return this.last().get('status') !== 'scheduled';
	}

	get last_completed() {
		let completed_rounds = this.filter(
			round => Rounds.isCompleted(round.get('status'))
		);

		return _.last(completed_rounds) || this.first();
	}

	get actual() {
		return this.getActualRound();
	}

	get active() {
		let active_round = _.first(this.filter(
			round => Rounds.isActive(round.get('status'))
		));

		return active_round;
	}

	get is_matches_completed() {
		const active_round = this.active;

		if (!active_round) {
			return false;
		}
		return _.size(
			_.filter(active_round.get('matches'), ({ status }) => status !== 'complete')
		) === 0;
	}

	get most_recent() {
		const first_match_status = _.result(this.active, 'attributes.matches.[0].status');
		return _.eq(first_match_status, 'complete') ? this.active : this.last_completed;
	}

	get last_round() {
		return this.active || this.last_completed;
	}

	get has_active() {
		return Boolean(this.active);
	}

	get next() {
		const next_round_id = this.actual.get('id') + 1;

		return this.get(next_round_id) || this.last();
	}

	get first_scheduled() {
		return this.getFirstScheduledRound();
	}

	get scheduled_rounds() {
		return this.onlyFutureRounds();
	}

	get non_scheduled_rounds() {
		return this.onlyNonScheduledRounds();
	}

	get bye_rounds() {
		// return this.filter(round => round.get('is_bye'));
		/* 
		There is now a bye in every round so we are using 
		the bye_squads field to check the bye status of teams here.
		*/
		return this;
	}

	get future_bye_rounds() {
		return this.bye_rounds.filter(
			round => round.get('status') === 'scheduled'
		);
	}

	get is_full_lockout() {
		return _.result(this.active, 'is_full_lockout', false);
	}

	get next_lockout_time() {
		return _.result(this.actual, 'next_match.date', this.next.next_match.date);
	}

	getActualRound() {
		let actual_round = this.find(
			round => Rounds.isActual(round.get('status'))
		);

		return actual_round || this.last();
	}

	get points_round() {
		return this.active || this.last_completed;
	}

	get is_current_round_bye() {
		return this.actual.is_bye;
	}

	getFirstScheduledRound() {
		let scheduled_round = this.find(
			round => Rounds.isScheduled(round.get('status'))
		);

		return scheduled_round || this.last();
	}

	/**
	 * Get rounds array with status === scheduled
	 * @returns {object} rounds
	 */
	onlyFutureRounds() {
		let rounds = this.filter(
			round => Rounds.isScheduled(round.get('status'))
		);

		return rounds;
	}

	onlyCompleteRounds() {
		let rounds = this.filter(
			round => Rounds.isCompleted(round.get('status'))
		);

		return rounds;
	}

	onlyNonScheduledRounds() {
		return this.filter(
			round => !Rounds.isScheduled(round.get('status'))
		);
	}

	/**
	 * Filter rounds by 'bye_rounds' field
	 * @param rounds
	 * @param isOnByeRounds
	 * @returns {object} new_rounds
	 */
	withoutByeRounds(rounds, isOnByeRounds) {
		let new_rounds = rounds.filter(round => {
			const is_bye_round = bye_rounds_ids.includes(round.id);
			return isOnByeRounds || !is_bye_round;
		});

		return new_rounds;
	}

	get actualRoundWithoutBye() {
		return this.find(round => {
			if (Rounds.isActual(round.get('status')) && !bye_rounds_ids.includes(round.get('id'))) {
				return round;
			}
		}) || this.last();
	}

	get activeRoundWithoutBye() {
		const reversed_models = this.models.reverse();

		return reversed_models.find(round => {
			if (
				(
					Rounds.isActive(round.get('status')) || Rounds.isCompleted(round.get('status'))
				) &&
				!bye_rounds_ids.includes(round.get('id'))
			) {
				return round;
			}
		}) || this.last();
	}

	/**
	 * Update model rounds field with filtered rounds
	 * @param isOnByeRounds
	 * @param onlyFuture - if we need only future rounds
	 * @param start_round - league start_round
	 * @param end_round - league end_round
	 * @param is_like_array - if we need to get rounds array, not models
	 * @returns {object} rounds
	 */
	recalculateRounds(isOnByeRounds, onlyFuture = true, {
		start_round = false, end_round = false, is_like_array = false
	} = {}) {
		let rounds = (onlyFuture) ? this.onlyFutureRounds() : this;
		rounds = this.withoutByeRounds(rounds, isOnByeRounds);

		if (start_round && end_round) {
			rounds = rounds.filter(function (round) {
				return round.get('id') >= start_round && round.get('id') <= end_round;
			});
		}

		if (is_like_array) {
			rounds = _.map(rounds, round => round.toJSON());
		}
		return rounds;
	}

	recalculateCompleteRounds(isOnByeRounds) {
		let rounds = this.onlyCompleteRounds(),
			actual_round = this.active;

		if (actual_round) {
			rounds.push(actual_round);
		}

		rounds = this.withoutByeRounds(rounds, isOnByeRounds);
		return rounds.map(round => round.toJSON());
	}

	static isActual(status) {
		return ACTUAL_ROUND_STATUSES.includes(status);
	}

	static isScheduled(status) {
		return SCHEDULED_ROUND_STATUSES.includes(status);
	}

	static isActive(status) {
		return ACTIVE_ROUND_STATUSES.includes(status);
	}

	static isCompleted(status) {
		return COMPLETED_ROUND_STATUSES.includes(status);
	}

	setSquadNames(squads) {
		const squads_by_id = _.keyBy(squads, 'id');
		this.each(round => {
			const matches = _.map(round.attributes.matches, match => {
				const { home_squad_id, away_squad_id } = match;
				return {
					...match,
					home_squad: squads_by_id[home_squad_id],
					away_squad: squads_by_id[away_squad_id]
				};
			});
			round.set({ matches });
		});

		return this;
	}
}

export default Rounds;